import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Loader2, X } from 'lucide-react';

import { KnowledgeParticleEmbeddedImageSection } from '../components/KnowledgeParticleEmbeddedImageSection';
import { KnowledgeParticleOpsMoreMenu } from '../components/KnowledgeParticleOpsMoreMenu';
import { KnowledgeParticleQaSections } from '../components/KnowledgeParticleQaSections';
import { useKnowledgeParticleSegmentChat } from '../hooks/useKnowledgeParticleSegmentChat';
import { KnowledgeParticlePapPanel } from '../components/KnowledgeParticlePapPanel';
import { knowledgeService } from '../services/knowledgeService';
import type { KnowledgeParticle } from '../types/domain';
import { getParticleTitle } from '../utils/particleDisplay';
import { resolveKnowledgeParticleBackLink } from '../utils/knowledgeNavigation';
import { readParticleSourceAssetId } from '../utils/embeddedImageMetadata';

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

/** API 偶发返回 {} / 嵌套对象，不能直接作为 React 子节点。 */
function asDisplayText(value: unknown, fallback = '—'): string {
  if (value == null) return fallback;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || fallback;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (Array.isArray(value)) {
    const parts = value
      .map((item) => asDisplayText(item, ''))
      .filter(Boolean);
    return parts.length ? parts.join('、') : fallback;
  }
  if (typeof value === 'object') {
    const keys = Object.keys(value as object);
    if (!keys.length) return fallback;
    try {
      return JSON.stringify(value);
    } catch {
      return fallback;
    }
  }
  return fallback;
}

export function KnowledgeDetailPage() {
  const { particleId = '' } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const backLink = resolveKnowledgeParticleBackLink(searchParams);
  const [particle, setParticle] = useState<KnowledgeParticle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [supplementOpen, setSupplementOpen] = useState(false);

  const loadParticle = useCallback(async () => {
    if (!particleId) return;
    setLoading(true);
    setError(null);
    try {
      setParticle(await knowledgeService.getKnowledgeParticle(particleId));
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, [particleId]);

  useEffect(() => {
    void loadParticle();
  }, [loadParticle]);

  const segmentChat = useKnowledgeParticleSegmentChat({
    entrypoint: 'kp-detail',
    surface: 'kp_detail',
    subject: particle?.subject,
    onParticleUpdated: (updated) => {
      setParticle(updated);
    },
  });

  const sourceAssetId = useMemo(
    () => readParticleSourceAssetId(asRecord(particle?.metadata)),
    [particle?.metadata],
  );

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="animate-spin text-primary" /></div>;
  if (!particle) return <p className="text-danger">{error || '未找到颗粒'}</p>;

  const headline = getParticleTitle(particle);
  const metadata = asRecord(particle.metadata);
  const subjectLabel = asDisplayText(particle.subject, '');
  const statusLabel = asDisplayText(particle.status, '');
  const createdAtLabel = asDisplayText(particle.created_at);
  const updatedAtLabel = asDisplayText(particle.updated_at);
  const errorFeedbackLabel = asDisplayText(metadata?.lastErrorFeedbackSummary, '');

  return (
    <div>
      <div className="sticky top-0 z-20 border-b border-border bg-canvas/95 pb-2 backdrop-blur">
        <div className="flex items-center gap-2">
          <Link to={backLink.to} className="shrink-0 text-sm text-primary">
            {backLink.label}
          </Link>
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-sm font-semibold text-ink" title={headline}>
              {headline}
            </h1>
            {subjectLabel ? (
              <p className="truncate text-[11px] text-muted">学科：{subjectLabel}</p>
            ) : null}
          </div>
          <KnowledgeParticleOpsMoreMenu
            particleId={particleId}
            subject={particle.subject}
            sourceAssetId={sourceAssetId}
            hideDetailLink
            menuPlacement="down"
            onOpenSupplement={() => setSupplementOpen(true)}
            onDeleted={() => navigate('/knowledge')}
          />
        </div>
      </div>

      <div className="space-y-6 pt-5">
        <KnowledgeParticleEmbeddedImageSection
          particle={particle}
          onParticleUpdated={setParticle}
        />

        <KnowledgeParticleQaSections
          particle={particle}
          segmentSelection={segmentChat.getSegmentSelection(particle)}
          fallbackQuestionText={particle.title}
        />

        <KnowledgeParticlePapPanel
          particleId={particleId}
          renderFooterActions={(ctx) => (
            <KnowledgeParticleOpsMoreMenu
              particleId={particleId}
              subject={particle.subject}
              sourceAssetId={sourceAssetId}
              hideDetailLink
              onOpenSupplement={() => setSupplementOpen(true)}
              papExtracting={ctx.extracting || ctx.running}
              showPapExtract={ctx.eligible && !ctx.hasContent && !ctx.running}
              showPapReExtract={ctx.eligible && (ctx.hasContent || ctx.failed)}
              onExtractPap={() => { void ctx.extract(false); }}
              onReExtractPap={() => { void ctx.extract(true); }}
              onDeleted={() => navigate('/knowledge')}
            />
          )}
        />
      </div>

      {supplementOpen ? (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/30 p-4 sm:items-center">
          <button type="button" className="absolute inset-0" aria-label="关闭" onClick={() => setSupplementOpen(false)} />
          <div className="relative z-10 w-full max-w-md rounded-2xl border border-border bg-surface p-4 shadow-xl">
            <div className="mb-3 flex items-center justify-between gap-2">
              <h2 className="text-sm font-semibold text-ink">颗粒详情</h2>
              <button
                type="button"
                className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted hover:bg-black/[0.04]"
                onClick={() => setSupplementOpen(false)}
              >
                <X size={16} />
              </button>
            </div>
            <dl className="space-y-2 text-sm">
              <div>
                <dt className="text-xs text-muted">ID</dt>
                <dd className="break-all text-ink">{asDisplayText(particle.id)}</dd>
              </div>
              <div>
                <dt className="text-xs text-muted">学科</dt>
                <dd className="text-ink">{subjectLabel || '—'}</dd>
              </div>
              <div>
                <dt className="text-xs text-muted">状态</dt>
                <dd className="text-ink">{statusLabel || '—'}</dd>
              </div>
              <div>
                <dt className="text-xs text-muted">素材</dt>
                <dd className="text-ink">{asDisplayText(sourceAssetId)}</dd>
              </div>
              <div>
                <dt className="text-xs text-muted">创建时间</dt>
                <dd className="text-ink">{createdAtLabel}</dd>
              </div>
              <div>
                <dt className="text-xs text-muted">更新时间</dt>
                <dd className="text-ink">{updatedAtLabel}</dd>
              </div>
              {errorFeedbackLabel ? (
                <div>
                  <dt className="text-xs text-muted">最近报错修订</dt>
                  <dd className="text-ink">{errorFeedbackLabel}</dd>
                </div>
              ) : null}
            </dl>
          </div>
        </div>
      ) : null}

      {segmentChat.overlays}
    </div>
  );
}
